"use client";

import type { ReactNode } from "react";
import { RollWidgetLayer } from "@/components/roll-widgets/roll-widget-layer";
import { useMinimapWidth, usePianoHeight } from "@/contexts/studio-context";

/**
 * Largeur du panneau latéral gauche, plafonnée à la moitié de la fenêtre.
 *
 * 200 px fixes conviennent au desktop, mais en tiroir sur un téléphone ils
 * mangent une part de l'écran, et le scrim étant opaque, cette part est prise
 * sur la seule zone qui permet de refermer le tiroir au doigt.
 *
 * **La borne était à 40 %, et elle a été mesurée depuis.** Écrite quand le
 * studio ne s'affichait jamais sur un téléphone debout, elle ne mordait alors
 * nulle part : en paysage comme au bureau, 40 vw dépasse largement 200 px.
 * Le premier écran où elle a servi est le replay en portrait, où elle donnait
 * 156 px sur 390 — et à 156 px la bande « Écouter » ne tient plus : ses quatre
 * segments réclament 160 px et le dernier se faisait couper. À la moitié, le
 * même écran donne 195 px, la bande rentre, la grille de mesures respire, et
 * il reste toujours 195 px de voile à taper pour refermer.
 *
 * Rien ne bouge ailleurs : la borne ne mord qu'en dessous de 400 px de large,
 * et le desktop comme le téléphone couché gardent leurs 200 px exacts.
 *
 * Une seule expression pour les deux usages (largeur en flux et feuille du
 * tiroir) : deux valeurs à garder en phase finissent toujours par diverger.
 */
const SIDEBAR_WIDTH = "min(200px, 50vw)";

interface StudioFrameProps {
  sidebar: ReactNode;
  studio: ReactNode;
  /** Whether the left sidebar is expanded. */
  sidebarOpen: boolean;
  /**
   * Compact (narrow-viewport) layout, i.e. anything below `expanded`. When
   * true the side panels float over the piano-roll as drawers (with a scrim)
   * instead of pushing the layout, so an "open" preference never strangles the
   * roll on small screens.
   */
  panelsCompact: boolean;
  /** Dismiss request from the drawer scrim (compact mode only). */
  onCloseSidebar?: () => void;
  /**
   * Standard overlay slot, clamped above the piano keys and to the
   * left of the minimap so the studio chrome (piano keyboard, minimap)
   * remains visible behind a translucent cover. Used by Settings,
   * Chords, Harmony, briefing, etc.
   */
  overlay?: ReactNode;
  /**
   * Full-canvas overlay slot, covers the entire studio area
   * including the piano keys and the minimap rail. Reserved for
   * presentation surfaces that take over the screen (e.g. the
   * piece overview "souffle"). Mutually exclusive with `overlay`
   * at every current call site; if both are set, this one wins
   * by virtue of being rendered last (same z-band).
   */
  fullCanvasOverlay?: ReactNode;
  /**
   * Rendered as-is inside the studio area, on top of `studio` and `overlay`.
   * Children must position themselves (e.g. count-in, section tips).
   */
  transient?: ReactNode;
  rightPanel?: ReactNode;
  /**
   * Whether `rightPanel` is expanded. The panel is mounted permanently (it
   * animates its own width), so the frame cannot infer it — and it needs to
   * know: the panel sits in an absolute layer *over* the roll, which on a
   * narrow viewport is an overlay with no way out unless a scrim is armed.
   */
  rightPanelOpen?: boolean;
  /** Dismiss request from the right-panel scrim (compact mode only). */
  onCloseRightPanel?: () => void;
}

export const StudioFrame = ({
  sidebar,
  studio,
  sidebarOpen,
  panelsCompact,
  onCloseSidebar,
  overlay,
  fullCanvasOverlay,
  transient,
  rightPanel,
  rightPanelOpen = false,
  onCloseRightPanel,
}: StudioFrameProps) => {
  // Live keyboard height so the overlay slot clamps exactly to the top of the
  // keys (the strip height scales with the clamped key width).
  const pianoHeight = usePianoHeight();
  // Idem pour le rail de minimap, qui se resserre sur un canvas étroit.
  const minimapWidth = useMinimapWidth();

  // Shared sidebar surface. Kept mounted in every state (only hidden) so the
  // section navigator / track list stay wired to the transport; collapsing
  // must not tear them down. `inert` pulls the hidden panel out of the tab
  // order and the a11y tree.
  const sidebarPanel = (
    <div
      id="studio-sidebar"
      className="h-full flex flex-col bg-bg-surface border-r border-border-subtle"
      style={{ width: SIDEBAR_WIDTH }}
      aria-hidden={!sidebarOpen}
      inert={!sidebarOpen}
    >
      {sidebar}
    </div>
  );

  return (
    <div className="flex-1 flex min-h-0 relative">
      {/*
        Toggling is instant by design — no width/transform transition. The
        piano-roll's horizontal layout scales with the canvas width (keys fill
        the available width), so a smooth animation would force a WebGL
        backing-store reallocation every frame, which stutters regardless of
        how cheap the per-frame draw is. Snapping resizes the canvas exactly
        once (via the Engine's ResizeObserver) — crisp, no jank.

        Compact: the panel floats over the roll as a drawer (with a scrim) so
        an "open" preference never strangles the roll on a narrow viewport.
        Desktop: the panel sits in flow and the roll reclaims the width.
      */}
      {/*
        Les deux voiles sont rendus ici, avant les panneaux, et non chacun à
        côté du sien. À `z-30` égal c'est l'ordre du DOM qui peint : un voile
        écrit après un tiroir passerait devant lui, et le tiroir, toujours
        visible, cesserait de recevoir le moindre clic — taper dessus fermerait
        l'autre panneau. Tous les voiles d'abord, tous les panneaux ensuite.

        Voile translucide, pas `bg-overlay-deep` : ce jeton est réservé aux
        calques qui *masquent* volontairement le roll (réglages, accords,
        harmonie). Ici on ne masque pas, on met en retrait — un voile opaque
        faisait disparaître tout le studio dès l'ouverture du tiroir, ce qui
        transforme un panneau latéral en changement de page.
      */}
      {panelsCompact && sidebarOpen && (
        <div
          className="absolute inset-0 z-30 bg-glass-backdrop"
          aria-hidden="true"
          onClick={onCloseSidebar}
        />
      )}

      {panelsCompact && rightPanel && rightPanelOpen && (
        <div
          className="absolute inset-0 z-30 bg-glass-backdrop"
          aria-hidden="true"
          onClick={onCloseRightPanel}
        />
      )}

      {panelsCompact ? (
        <>
          <div
            className={`absolute inset-y-0 left-0 z-30 ${
              sidebarOpen ? "" : "hidden"
            }`}
          >
            {sidebarPanel}
          </div>
        </>
      ) : (
        <div
          className="shrink-0 overflow-hidden"
          style={{ width: sidebarOpen ? SIDEBAR_WIDTH : 0 }}
        >
          {sidebarPanel}
        </div>
      )}

      <div className="flex-1 min-w-0 min-h-0 relative">
        {studio}

        {/*
          L'échelle des calques de la zone studio, du fond vers la surface :
          le roll, les transients à `z-20` (count-in, interstitiel de section,
          panneaux du parcours), les calques qui masquent le roll à `z-24`, le
          cadre de dépôt à `z-25`, les tiroirs et le panneau droit à `z-30`.

          `z-24` et non `z-20` : à égalité c'est l'ordre du DOM qui peint, et
          le slot `transient` est écrit après celui-ci. Les réglages ouverts
          depuis un parcours se glissaient donc sous les panneaux du parcours,
          qui couvrent l'essentiel du roll : l'écran s'ouvrait bel et bien,
          mais on ne le voyait pas. Un calque qui masque le roll est par
          définition au-dessus de ce qui flotte dessus, il le dit donc avec un
          nombre et non avec sa position dans le fichier.
        */}
        {overlay && (
          <div
            className="absolute left-0 top-0 z-24 bg-overlay-deep"
            style={{
              bottom: pianoHeight,
              right: minimapWidth,
            }}
          >
            {overlay}
          </div>
        )}

        {fullCanvasOverlay && (
          <div className="absolute inset-0 z-24 bg-overlay-deep">
            {fullCanvasOverlay}
          </div>
        )}

        {transient}

        {/* Les widgets posés sur le roll (`RollWidget`), après les transients. */}
        <RollWidgetLayer bottom={pianoHeight} right={minimapWidth} />
      </div>

      {rightPanel && (
        /*
          Le panneau droit est posé en absolu : il recouvre le roll au lieu de
          le rétrécir. Sur desktop c'est sans conséquence, il reste de la place
          autour ; sur écran étroit il prend la moitié de la vue et l'ouvrir
          devient un cul-de-sac, sa croix de fermeture étant le seul chemin de
          retour. Son voile (plus haut) rend la sortie évidente au doigt — même
          geste que le tiroir de gauche, donc rien de neuf à apprendre.
        */
        <div className="absolute inset-y-0 right-0 z-30 pointer-events-none">
          <div className="h-full pointer-events-auto">{rightPanel}</div>
        </div>
      )}
    </div>
  );
};
